import { useNavigate } from 'react-router-dom';
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable react/no-danger */
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTable } from '@fortawesome/free-solid-svg-icons';
import { Client } from '@types';
import {
  ArSnapshot,
  ArInvoice,
  ArInvoiceComment,
} from '@shared/types/arSnapshot';
import { get } from '@/utils/queries';
import { post, fetchDelete } from '@/utils/api';
import { alertErrorMessage, alertMessageSuccess } from '@/utils/alerts';
import { Spinner } from '@/components/Spinner';
import ErrorAlert from '@/components/ErrorAlert/ErrorAlert';
import PageContainer from '@/components/PageContainer';
import { DashboardWrapper } from '@/components/DashboardWrapper';
import { IUserContext, useUserContext } from '@/context/UserContext';
import { CompanySelectorDropdown } from '../Transactions/CompanySelector';
import './ArDashboard.css';

/*
 * AR Command Center — template, driven by the
 * live QBO snapshot. READ-ONLY: every write feature from the prototype
 * (inline edits, status editor, reminder modal, meeting-note editing, CSV
 * export) is intentionally omitted. Fields whose source is not yet defined
 * (CRM data, the free-text invoice note, the AP contact person, the internal
 * owner) render a `Need definition` marker instead of a value.
 */

/* eslint-disable max-len */
const OPSTART_MARK = `<svg viewBox="0 0 601 699" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M169.43 415.199C172.611 420.636 176.093 425.948 179.894 431.097C188.794 443.154 188.997 460.233 178.398 470.828L167.41 481.811C166.676 482.545 165.993 483.313 165.36 484.106L62.6603 586.806C63.3555 585.909 64.1098 585.043 64.9325 584.221L75.9203 573.24C86.5211 562.646 86.4095 545.515 76.7192 534.082C72.6834 529.322 68.8081 524.469 65.0908 519.538L169.43 415.199Z" fill="#2BB073"/>
<path d="M519.706 64.9252C524.697 68.6781 529.607 72.592 534.424 76.6703C545.862 86.3543 545.808 103.651 535.208 114.246L524.22 125.23C524.055 125.395 523.898 125.566 523.738 125.735L420.288 229.18C420.676 228.738 421.079 228.302 421.501 227.88L432.489 216.9C443.184 206.21 443.167 188.641 430.873 179.837C425.719 176.146 420.403 172.763 414.964 169.667L519.706 64.9252Z" fill="#2BB073"/>
<path d="M95.1207 95.0581C210.942 -20.6887 392.478 -30.7304 519.706 64.9252L414.964 169.667C345.981 130.404 256.079 139.02 197.594 197.467C138.529 256.496 129.147 346.363 169.43 415.199L65.0908 519.538C-30.7602 392.383 -20.7507 210.855 95.1207 95.0581Z" fill="#43F192"/>
<path d="M536.211 113.185C535.886 113.545 535.555 113.899 535.208 114.246L524.22 125.23C513.62 135.824 513.732 152.953 523.422 164.385C631.518 291.903 625.383 483.121 505.019 603.409C384.654 723.697 193.316 729.825 65.7172 621.797C55.0597 612.775 54.3789 597.145 62.9099 586.483L165.655 483.738C156.836 494.412 157.41 510.313 168.767 518.688C239.151 570.585 338.814 564.69 402.545 501C465.806 437.779 470.738 337.811 419.788 267.315C411.178 255.401 411.002 238.733 421.182 228.213L536.211 113.185Z" fill="#D9FCE9"/>
</svg>`;
/* eslint-enable max-len */

const MONTHS = new Set([
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
]);

// ── helpers (amounts are integer CENTS in the snapshot) ──
const formatUSD = (cents: number | null): string =>
  cents == null || Number.isNaN(cents)
    ? '—'
    : `$${Math.round(cents / 100).toLocaleString('en-US')}`;

const parseD = (s: string | null): Date | null =>
  s ? new Date(`${s}T00:00:00`) : null;
const formatDate = (s: string | null): string => {
  const d = parseD(s);
  return d
    ? d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';
};
const formatDateshort = (s: string | null): string => {
  const d = parseD(s);
  return d
    ? d.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
      })
    : '—';
};
const daysOverdue = (inv: ArInvoice, asOf: string): number | null => {
  const due = parseD(inv.dueDate);
  const ref = parseD(asOf);
  if (!due || !ref) return null;
  return Math.round((ref.getTime() - due.getTime()) / 86_400_000);
};
const outstanding = (inv: ArInvoice): number =>
  Math.max(0, (inv.amount ?? 0) - (inv.amountPaid ?? 0));
const isOpen = (s: string): boolean =>
  s === 'Overdue' || s === 'Outstanding' || s === 'Partial';
const isSched = (inv: ArInvoice): boolean =>
  MONTHS.has((inv.status || '').toUpperCase());

function NeedDef() {
  return <span className='needdef'>Need definition</span>;
}

function StatusChip({ status }: { status: string }) {
  const up = (status || '').toUpperCase();
  if (MONTHS.has(up)) return <span className='chip sched'>{status}</span>;
  if (status === 'Possible Renewal')
    return <span className='chip ch'>Poss. Renewal</span>;
  const cls = [
    'Overdue',
    'Outstanding',
    'PAID',
    'Partial',
    'Hold',
    'Churned',
    'Void',
  ].includes(status)
    ? status
    : 'Outstanding';
  return <span className={`chip ${cls}`}>{status}</span>;
}

function ChannelTag({ inv }: { inv: ArInvoice }) {
  if (!inv.channel) return <>—</>;
  return (
    <>
      <span className={`tag ${inv.channel}`}>{inv.channel}</span>
      {inv.partner && inv.partner !== 'N/A' ? (
        <span className='muted' style={{ fontSize: 11, marginLeft: 6 }}>
          {inv.partner}
        </span>
      ) : null}
    </>
  );
}

// Contact = who the invoice was sent to (the invoice's BillEmail).
function ContactCell({ inv }: { inv: ArInvoice }) {
  return inv.email ? (
    <span className='contact-cell'>{inv.email}</span>
  ) : (
    <NeedDef />
  );
}

// "Days late" cell: overdue → "Nd over", future-dated → "in Nd", else "—".
function DaysLateCell({ inv, asOf }: { inv: ArInvoice; asOf: string }) {
  const d = daysOverdue(inv, asOf);
  if (inv.status === 'Overdue' && d != null && d > 0) {
    return <span style={{ color: 'var(--over)' }}>{d}d over</span>;
  }
  if (d != null && d < 0) {
    return <span style={{ color: 'var(--txt-faint)' }}>in {-d}d</span>;
  }
  return <>—</>;
}

type View = 'dashboard' | 'tracker' | 'customers' | 'history' | 'notes';

// Minimum time the refresh button stays in its busy state, so the spinner is
// visibly shown even though the endpoint returns 202 almost immediately.
const REFRESH_MIN_SPIN_MS = 1500;
const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

// Inline-styled so it survives the `.arcc` reset (which zeroes descendant
// padding) and works both inside the dashboard topbar and in the empty state.
// Colors mirror the .arcc theme tokens.
function RefreshButton({
  onClick,
  isRefreshing,
  label,
  busyLabel = 'Refreshing…',
  primary = false,
}: {
  onClick: () => void;
  isRefreshing: boolean;
  label: string;
  busyLabel?: string;
  primary?: boolean;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={isRefreshing}
      style={{
        padding: '8px 16px',
        borderRadius: 10,
        border: '1px solid #16463d',
        background: primary ? '#0a6b5e' : '#0a312b',
        color: '#f6f6f6',
        font: 'inherit',
        fontSize: 13,
        fontWeight: 600,
        cursor: isRefreshing ? 'default' : 'pointer',
        opacity: isRefreshing ? 0.6 : 1,
      }}
    >
      {isRefreshing ? (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <Spinner className='w-4 h-4' />
          <span style={{ marginLeft: '4px' }}>{busyLabel}</span>
        </span>
      ) : (
        label
      )}
    </button>
  );
}

function ArDashboardView({
  snapshot,
  selector,
  onRefresh,
  isRefreshing,
}: {
  snapshot: ArSnapshot;
  selector?: ReactNode;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}) {
  const { asOf } = snapshot;
  const currentUserId = useUserContext().auth?.id ?? null;
  const [view, setView] = useState<View>('dashboard');
  const [present, setPresent] = useState(false);
  const [drawer, setDrawer] = useState<
    { kind: 'invoice'; id: string } | { kind: 'customer'; name: string } | null
  >(null);
  // Locally-applied note edits, so add/delete shows immediately without
  // re-fetching the whole snapshot.
  const [commentOverrides, setCommentOverrides] = useState<
    Record<string, ArInvoiceComment[]>
  >({});

  const invoices = useMemo(
    () =>
      snapshot.invoices.map((inv) =>
        inv.invoiceId in commentOverrides
          ? { ...inv, comments: commentOverrides[inv.invoiceId] }
          : inv
      ),
    [snapshot.invoices, commentOverrides]
  );

  // Current notes for an invoice, preferring any local override over the blob.
  const commentsFor = (
    prev: Record<string, ArInvoiceComment[]>,
    invoiceId: string
  ) =>
    invoiceId in prev
      ? prev[invoiceId]
      : snapshot.invoices.find((i) => i.invoiceId === invoiceId)?.comments ??
        [];

  const handleAddComment = async (invoiceId: string, body: string) => {
    try {
      const res = await post<{ comment: ArInvoiceComment }>('qbo/ar-comments', {
        clientId: snapshot.client.clientId,
        companyId: snapshot.client.companyId,
        invoiceId,
        body,
      });
      setCommentOverrides((prev) => ({
        ...prev,
        [invoiceId]: [...commentsFor(prev, invoiceId), res.comment],
      }));
      alertMessageSuccess('Note added.');
    } catch {
      alertErrorMessage('Could not add the note. Please try again.');
    }
  };

  const handleDeleteComment = async (invoiceId: string, commentId: number) => {
    try {
      // clientId/companyId go in the query (not a DELETE body — some proxies drop it).
      const query = new URLSearchParams({
        clientId: String(snapshot.client.clientId),
        companyId: String(snapshot.client.companyId),
      });
      await fetchDelete(`qbo/ar-comments/${commentId}?${query.toString()}`);
      setCommentOverrides((prev) => ({
        ...prev,
        [invoiceId]: commentsFor(prev, invoiceId).filter(
          (c) => c.id !== commentId
        ),
      }));
      alertMessageSuccess('Note deleted.');
    } catch {
      alertErrorMessage('Could not delete the note. Please try again.');
    }
  };

  const ACTIVE = useMemo(() => invoices.filter((r) => r.active), [invoices]);
  const PAID_HIST = useMemo(
    () => invoices.filter((r) => r.status === 'PAID' && !r.active),
    [invoices]
  );

  return (
    <div className='arcc'>
      <div className='app'>
        <nav className='rail'>
          <div
            className='logo'
            dangerouslySetInnerHTML={{ __html: OPSTART_MARK }}
          />
          <RailBtn
            label='Dashboard'
            active={view === 'dashboard'}
            onClick={() => setView('dashboard')}
          >
            <rect x='3' y='3' width='7' height='9' rx='1.5' />
            <rect x='14' y='3' width='7' height='5' rx='1.5' />
            <rect x='14' y='12' width='7' height='9' rx='1.5' />
            <rect x='3' y='16' width='7' height='5' rx='1.5' />
          </RailBtn>
          <RailBtn
            label='Invoice Tracker'
            active={view === 'tracker'}
            onClick={() => setView('tracker')}
          >
            <line x1='3' y1='6' x2='21' y2='6' />
            <line x1='3' y1='12' x2='21' y2='12' />
            <line x1='3' y1='18' x2='21' y2='18' />
          </RailBtn>
          <RailBtn
            label='Customers'
            active={view === 'customers'}
            onClick={() => setView('customers')}
          >
            <circle cx='9' cy='8' r='3.2' />
            <path d='M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5' />
            <path d='M16 6.5a3 3 0 0 1 0 5.5M18 19c0-2-.8-3.6-2-4.5' />
          </RailBtn>
          <RailBtn
            label='Paid History'
            active={view === 'history'}
            onClick={() => setView('history')}
          >
            <path d='M3 12a9 9 0 1 0 3-6.7L3 8' />
            <path d='M3 4v4h4' />
            <path d='M12 8v4l3 2' />
          </RailBtn>
          <RailBtn
            label='Meeting Notes'
            active={view === 'notes'}
            onClick={() => setView('notes')}
          >
            <path d='M4 4h16v16H4z' />
            <line x1='8' y1='9' x2='16' y2='9' />
            <line x1='8' y1='13' x2='16' y2='13' />
            <line x1='8' y1='17' x2='12' y2='17' />
          </RailBtn>
          <div style={{ flex: 1 }} />
          <button
            type='button'
            onClick={() => setPresent(true)}
            style={{
              background: 'rgba(227,159,0,.12)',
              color: 'var(--gold-soft)',
            }}
          >
            <svg viewBox='0 0 24 24' fill='none' stroke='currentColor'>
              <path d='M2 4h20v13H2z' />
              <path d='M12 17v3M8 20h8' />
            </svg>
            <span className='tip'>Present Mode</span>
          </button>
        </nav>

        <main className='main'>
          <div className='wrap'>
            <div className='topbar'>
              <div className='title-block'>
                <div className='cobrand'>
                  <div
                    className='ob-logo'
                    style={{ width: 26 }}
                    dangerouslySetInnerHTML={{ __html: OPSTART_MARK }}
                  />
                  <div className='x-div' />
                  <div className='client'>
                    <div className='cname'>
                      {snapshot.client.companyName ??
                        `Company ${snapshot.client.companyId}`}
                    </div>
                  </div>
                </div>
                <div className='sub'>AR Dashboard</div>
              </div>
              <div className='spacer' />
              {onRefresh ? (
                <div style={{ marginRight: 16 }}>
                  <RefreshButton
                    onClick={onRefresh}
                    isRefreshing={!!isRefreshing}
                    label='Refresh'
                  />
                </div>
              ) : null}
              {selector ? (
                <div style={{ marginRight: 16 }}>{selector}</div>
              ) : null}
              <div className='updated'>
                AS OF
                <br />
                <b>{formatDate(asOf)}</b>
              </div>
            </div>

            {view === 'dashboard' && (
              <Dashboard
                active={ACTIVE}
                asOf={asOf}
                onRow={(id) => setDrawer({ kind: 'invoice', id })}
              />
            )}
            {view === 'tracker' && (
              <Tracker
                all={invoices}
                active={ACTIVE}
                asOf={asOf}
                onRow={(id) => setDrawer({ kind: 'invoice', id })}
              />
            )}
            {view === 'customers' && (
              <Customers
                all={invoices}
                onCustomer={(name) => setDrawer({ kind: 'customer', name })}
              />
            )}
            {view === 'history' && (
              <History
                paidHist={PAID_HIST}
                onRow={(id) => setDrawer({ kind: 'invoice', id })}
              />
            )}
            {view === 'notes' && <NotesPlaceholder />}
          </div>
        </main>
      </div>

      {drawer?.kind === 'invoice' && (
        <InvoiceDrawer
          inv={invoices.find((r) => r.invoiceId === drawer.id)}
          currentUserId={currentUserId}
          asOf={asOf}
          onAddComment={(body) => handleAddComment(drawer.id, body)}
          onDeleteComment={(id) => handleDeleteComment(drawer.id, id)}
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer?.kind === 'customer' && (
        <CustomerDrawer
          name={drawer.name}
          all={invoices}
          onInvoice={(id) => setDrawer({ kind: 'invoice', id })}
          onClose={() => setDrawer(null)}
        />
      )}

      {present && (
        <PresentMode
          active={ACTIVE}
          asOf={asOf}
          onClose={() => setPresent(false)}
        />
      )}
    </div>
  );
}

function RailBtn({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button type='button' className={active ? 'on' : ''} onClick={onClick}>
      <svg viewBox='0 0 24 24' fill='none' stroke='currentColor'>
        {children}
      </svg>
      <span className='tip'>{label}</span>
    </button>
  );
}

// ════════════ DASHBOARD ════════════
function Dashboard({
  active,
  asOf,
  onRow,
}: {
  active: ArInvoice[];
  asOf: string;
  onRow: (id: string) => void;
}) {
  const kpis = useMemo(() => {
    let overdue = 0;
    let out = 0;
    let sched = 0;
    active.forEach((r) => {
      if (r.status === 'Overdue') overdue += outstanding(r);
      else if (r.status === 'Outstanding' || r.status === 'Partial')
        out += outstanding(r);
      else if (isSched(r)) sched += r.amount ?? 0;
    });
    const sent = overdue + out;
    return { overdue, out, sched, sent, total: sent + sched };
  }, [active]);

  const overdueCount = active.filter((r) => r.status === 'Overdue').length;
  const cards = [
    {
      cls: 'accent-over',
      lab: 'Overdue',
      val: formatUSD(kpis.overdue),
      meta: `${overdueCount} invoices past due`,
    },
    {
      cls: 'accent-out',
      lab: 'Outstanding',
      val: formatUSD(kpis.out),
      meta: 'sent · not yet due',
    },
    // Scheduled — no way to derive scheduled/future invoices from QBO for now.
    // {
    //   cls: 'accent-sched',
    //   lab: 'Scheduled',
    //   val: formatUSD(kpis.sched),
    //   meta: 'month-tagged · upcoming',
    // },
    {
      cls: 'accent-total',
      lab: 'Sent (AR)',
      val: formatUSD(kpis.sent),
      meta: 'overdue + outstanding',
    },
    // Total pipeline = sent + scheduled, but scheduled has no QBO source yet
    // (see the Scheduled card above), so `sched` is always 0 and this would just
    // duplicate "Sent (AR)". Commented out until scheduled has a source.
    // NOTE: keep the active KPI count in sync with `.kpi-row` grid columns (CSS).
    // {
    //   cls: 'accent-paid',
    //   lab: 'Total pipeline',
    //   val: formatUSD(kpis.total),
    //   meta: 'sent + scheduled',
    // },
  ];

  // aging waterfall
  const buckets = [
    { l: '1–30 days', min: 1, max: 30, c: '#FFD23F' },
    { l: '31–60 days', min: 31, max: 60, c: '#FFA23D' },
    { l: '61–90 days', min: 61, max: 90, c: '#FF6B3D' },
    { l: '91+ days', min: 91, max: 99999, c: '#FF3D5A' },
  ];
  const bucketVals = buckets.map((b) => {
    let s = 0;
    active.forEach((r) => {
      if (r.status === 'Overdue') {
        const d = daysOverdue(r, asOf);
        if (d != null && d >= b.min && d <= b.max) s += outstanding(r);
      }
    });
    return s;
  });
  const bucketMax = Math.max(...bucketVals, 1);
  const ageTotal = bucketVals.reduce((a, b) => a + b, 0);

  // channel mix
  const byPartner: Record<string, number> = {};
  active
    .filter((r) => isOpen(r.status))
    .forEach((r) => {
      const key = r.channel === 'Direct' ? 'Direct' : r.partner || 'Partner';
      byPartner[key] = (byPartner[key] || 0) + outstanding(r);
    });
  const mix = Object.entries(byPartner).sort((a, b) => b[1] - a[1]);
  const mixTot = mix.reduce((a, b) => a + b[1], 0) || 1;
  const colors = [
    '#1FB89A',
    '#ED3E94',
    '#5BA8FF',
    '#FFD23F',
    '#B98CFF',
    '#3DD4E0',
    '#FF8A3D',
  ];

  const attn = [...active.filter((r) => r.status === 'Overdue')].sort(
    (a, b) => (daysOverdue(b, asOf) ?? 0) - (daysOverdue(a, asOf) ?? 0)
  );

  return (
    <section className='view on'>
      <div className='kpi-row'>
        {cards.map((c) => (
          <div className={`kpi ${c.cls}`} key={c.lab}>
            <div className='lab'>{c.lab}</div>
            <div className='val'>{c.val}</div>
            <div className='meta'>{c.meta}</div>
          </div>
        ))}
      </div>

      <div className='grid cols-2'>
        <div className='panel'>
          <h2>
            <span className='dot' />
            Overdue aging waterfall
            <span className='count'>{formatUSD(ageTotal)}</span>
          </h2>
          <div className='fall'>
            {buckets.map((b, i) => (
              <div className='fall-row' key={b.l}>
                <div className='fl'>{b.l}</div>
                <div className='fall-track'>
                  <div
                    className='fall-bar'
                    style={{
                      width: `${(bucketVals[i] / bucketMax) * 100}%`,
                      background: `linear-gradient(90deg,${b.c}aa,${b.c})`,
                    }}
                  >
                    <span>
                      {bucketVals[i] > 0 ? formatUSD(bucketVals[i]) : ''}
                    </span>
                  </div>
                </div>
                <div
                  className='fall-amt'
                  style={{
                    color: bucketVals[i] > 0 ? b.c : 'var(--txt-faint)',
                  }}
                >
                  {formatUSD(bucketVals[i])}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='panel'>
          <h2>
            <span className='dot' style={{ background: 'var(--pink)' }} />
            Channel mix (active)
            <span className='count'>{mix.length} channels</span>
          </h2>
          <div
            style={{
              display: 'flex',
              height: 13,
              borderRadius: 7,
              overflow: 'hidden',
              marginBottom: 18,
              border: '1px solid var(--line)',
            }}
          >
            {mix.map((e, i) => (
              <div
                key={e[0]}
                style={{
                  width: `${(e[1] / mixTot) * 100}%`,
                  background: colors[i % colors.length],
                }}
                title={`${e[0]}: ${formatUSD(e[1])}`}
              />
            ))}
          </div>
          {mix.map((e, i) => (
            <div
              key={e[0]}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '7px 0',
                borderBottom: '1px solid var(--line)',
              }}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 3,
                  background: colors[i % colors.length],
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 12.5, fontWeight: 500 }}>{e[0]}</span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontFamily: 'var(--mono)',
                  fontSize: 12,
                  color: 'var(--txt-dim)',
                }}
              >
                {formatUSD(e[1])}
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--txt-faint)',
                  width: 38,
                  textAlign: 'right',
                }}
              >
                {Math.round((e[1] / mixTot) * 100)}%
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className='panel' style={{ marginTop: 14 }}>
        <h2>
          <span className='dot' style={{ background: 'var(--over)' }} />
          Needs attention now
          <span className='count'>{attn.length} overdue</span>
        </h2>
        <div className='tbl-wrap'>
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Channel</th>
                <th>Sent</th>
                <th className='num'>Amount</th>
                <th className='num'>Days</th>
                <th>Contact</th>
              </tr>
            </thead>
            <tbody>
              {attn.map((r) => {
                const d = daysOverdue(r, asOf);
                return (
                  <tr key={r.invoiceId} onClick={() => onRow(r.invoiceId)}>
                    <td className='cust'>{r.customer}</td>
                    <td>
                      <ChannelTag inv={r} />
                    </td>
                    <td>{formatDateshort(r.sendDate)}</td>
                    <td className='num'>{formatUSD(r.amount)}</td>
                    <td className='num'>
                      {d != null && d > 0 ? (
                        <span style={{ color: 'var(--over)' }}>{d}d over</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <ContactCell inv={r} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

// ════════════ TRACKER ════════════
function Tracker({
  all,
  active,
  asOf,
  onRow,
}: {
  all: ArInvoice[];
  active: ArInvoice[];
  asOf: string;
  onRow: (id: string) => void;
}) {
  const [mode, setMode] = useState<'active' | 'all'>('active');
  const [statuses, setStatuses] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');

  const toggleStatus = (s: string) =>
    setStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  const rows = useMemo(() => {
    let base = mode === 'active' ? active : all;
    if (statuses.size) {
      base = base.filter((r) => {
        if (statuses.has('Scheduled') && isSched(r)) return true;
        return statuses.has(r.status);
      });
    }
    if (query) {
      const q = query.toLowerCase();
      base = base.filter((r) =>
        [
          r.customer,
          r.partner,
          r.channel,
          r.sendDate,
          r.dueDate,
          r.status,
        ].some((v) => (v || '').toString().toLowerCase().includes(q))
      );
    }
    return [...base].sort((a, b) =>
      (a.sendDate || '') < (b.sendDate || '') ? -1 : 1
    );
  }, [all, active, mode, statuses, query]);

  // 'Scheduled' omitted — no way to derive scheduled invoices from QBO for now.
  const chips = ['Overdue', 'Outstanding', 'PAID', 'Partial', 'Hold'];

  return (
    <section className='view on'>
      <div className='toolbar'>
        <div className='seg'>
          <button
            type='button'
            className={mode === 'active' ? 'on' : ''}
            onClick={() => setMode('active')}
          >
            Active view
          </button>
          <button
            type='button'
            className={mode === 'all' ? 'on' : ''}
            onClick={() => setMode('all')}
          >
            All invoices
          </button>
        </div>
        <div className='chip-filter'>
          {chips.map((s) => (
            <div
              key={s}
              className={`cf ${statuses.has(s) ? 'on' : ''}`}
              onClick={() => toggleStatus(s)}
            >
              {s}
            </div>
          ))}
        </div>
        <div className='spacer' style={{ flex: 1 }} />
        <div className='search-wrap'>
          <svg
            className='search-ico'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
          >
            <circle cx='11' cy='11' r='7' />
            <line x1='21' y1='21' x2='16.5' y2='16.5' />
          </svg>
          <input
            className='search'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search customer, partner…'
          />
        </div>
      </div>
      <div className='tbl-wrap'>
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Channel</th>
              <th>Sent</th>
              <th>Due</th>
              <th className='num'>Amount</th>
              <th className='num'>Outstanding</th>
              <th>Status</th>
              <th className='num'>Days</th>
              <th>Contact</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.invoiceId} onClick={() => onRow(r.invoiceId)}>
                <td className='cust'>{r.customer}</td>
                <td>
                  <ChannelTag inv={r} />
                </td>
                <td>{formatDateshort(r.sendDate)}</td>
                <td>{formatDateshort(r.dueDate)}</td>
                <td className='num'>{formatUSD(r.amount)}</td>
                <td className='num'>{formatUSD(outstanding(r))}</td>
                <td>
                  <StatusChip status={r.status} />
                </td>
                <td className='num'>
                  <DaysLateCell inv={r} asOf={asOf} />
                </td>
                <td>
                  <ContactCell inv={r} />
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={9}>
                  <div className='empty'>
                    <div className='big'>Nothing here</div>
                    <div>No invoices match this view.</div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ════════════ CUSTOMERS ════════════
interface CustAgg {
  customer: string;
  channel: string;
  partner: string;
  count: number;
  paid: number;
  open: number;
}
function aggregateCustomers(all: ArInvoice[]): CustAgg[] {
  const map: Record<string, CustAgg> = {};
  all.forEach((r) => {
    if (!r.customer) return;
    if (!map[r.customer]) {
      map[r.customer] = {
        customer: r.customer,
        channel: r.channel,
        partner: r.partner,
        count: 0,
        paid: 0,
        open: 0,
      };
    }
    const m = map[r.customer];
    m.count += 1;
    if (r.status === 'PAID') m.paid += r.amount ?? 0;
    if (isOpen(r.status) && r.active) m.open += outstanding(r);
  });
  return Object.values(map);
}
function Customers({
  all,
  onCustomer,
}: {
  all: ArInvoice[];
  onCustomer: (name: string) => void;
}) {
  const [sort, setSort] = useState<'paid' | 'open' | 'name'>('paid');
  const [query, setQuery] = useState('');
  const custs = useMemo(() => {
    let list = aggregateCustomers(all);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(
        (c) =>
          c.customer.toLowerCase().includes(q) ||
          (c.partner || '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      if (sort === 'name') return a.customer.localeCompare(b.customer);
      if (sort === 'open') return b.open - a.open;
      return b.paid - a.paid;
    });
  }, [all, sort, query]);

  return (
    <section className='view on'>
      <div className='toolbar'>
        <div className='seg'>
          <button
            type='button'
            className={sort === 'paid' ? 'on' : ''}
            onClick={() => setSort('paid')}
          >
            By lifetime paid
          </button>
          <button
            type='button'
            className={sort === 'open' ? 'on' : ''}
            onClick={() => setSort('open')}
          >
            By open balance
          </button>
          <button
            type='button'
            className={sort === 'name' ? 'on' : ''}
            onClick={() => setSort('name')}
          >
            A–Z
          </button>
        </div>
        <div className='spacer' style={{ flex: 1 }} />
        <div className='search-wrap'>
          <svg
            className='search-ico'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
          >
            <circle cx='11' cy='11' r='7' />
            <line x1='21' y1='21' x2='16.5' y2='16.5' />
          </svg>
          <input
            className='search'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search customer…'
          />
        </div>
      </div>
      <div className='cust-grid'>
        {custs.map((c) => (
          <div
            className='cust-card'
            key={c.customer}
            onClick={() => onCustomer(c.customer)}
          >
            <div className='cn'>{c.customer}</div>
            <div className='cmeta'>
              {c.channel}
              {c.partner && c.partner !== 'N/A' ? ` · ${c.partner}` : ''} ·{' '}
              {c.count} invoice{c.count > 1 ? 's' : ''}
            </div>
            <div className='cstats'>
              <div className='cst'>
                <div className='csv'>{formatUSD(c.paid)}</div>
                <div className='csl'>Lifetime paid</div>
              </div>
              <div className='cst'>
                <div
                  className='csv'
                  style={{ color: c.open > 0 ? 'var(--over)' : 'var(--txt)' }}
                >
                  {formatUSD(c.open)}
                </div>
                <div className='csl'>Open</div>
              </div>
              {/* ARR — not sourced from QBO for now.
              <div className='cst'>
                <div className='csv' style={{ fontSize: 12 }}>
                  <NeedDef />
                </div>
                <div className='csl'>ARR</div>
              </div> */}
            </div>
          </div>
        ))}
        {custs.length === 0 && (
          <div className='empty'>
            <div className='big'>No customers found</div>
          </div>
        )}
      </div>
    </section>
  );
}

// ════════════ HISTORY ════════════
function History({
  paidHist,
  onRow,
}: {
  paidHist: ArInvoice[];
  onRow: (id: string) => void;
}) {
  const years = useMemo(
    () =>
      [
        ...new Set(
          paidHist
            .map((r) => r.datePaid?.slice(0, 4))
            .filter(Boolean) as string[]
        ),
      ]
        .sort()
        .reverse(),
    [paidHist]
  );
  const [year, setYear] = useState<'all' | string>('all');
  const rows = useMemo(() => {
    const list =
      year === 'all'
        ? paidHist
        : paidHist.filter((r) => r.datePaid?.slice(0, 4) === year);
    return [...list].sort((a, b) =>
      (b.datePaid || '') < (a.datePaid || '') ? -1 : 1
    );
  }, [paidHist, year]);
  const total = rows.reduce((a, b) => a + (b.amount ?? 0), 0);

  return (
    <section className='view on'>
      <div className='toolbar'>
        <div className='seg'>
          <button
            type='button'
            className={year === 'all' ? 'on' : ''}
            onClick={() => setYear('all')}
          >
            All years
          </button>
          {years.map((y) => (
            <button
              type='button'
              key={y}
              className={year === y ? 'on' : ''}
              onClick={() => setYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
        <div className='spacer' style={{ flex: 1 }} />
        <span className='updated'>
          {rows.length} paid · {formatUSD(total)} collected
        </span>
      </div>
      <div className='tbl-wrap'>
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Channel</th>
              <th>Sent</th>
              <th>Date Paid</th>
              <th className='num'>Amount</th>
              <th>Invoice Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.invoiceId} onClick={() => onRow(r.invoiceId)}>
                <td className='cust'>{r.customer}</td>
                <td>
                  <ChannelTag inv={r} />
                </td>
                <td>{formatDateshort(r.sendDate)}</td>
                <td>{formatDateshort(r.datePaid)}</td>
                <td className='num'>{formatUSD(r.amount)}</td>
                <td>
                  {r.invoiceStatus ? (
                    <span style={{ whiteSpace: 'pre-line' }}>
                      {r.invoiceStatus}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ════════════ NOTES (no data source) ════════════
function NotesPlaceholder() {
  return (
    <section className='view on'>
      <div className='panel'>
        <h2>
          <span className='dot' style={{ background: 'var(--northwind)' }} />
          Meeting Notes
        </h2>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '20px 0',
          }}
        >
          <NeedDef />
          <span
            style={{ color: 'var(--txt-dim)', fontSize: 13, lineHeight: 1.6 }}
          >
            The prototype stored meeting notes (topics, action items, attendees)
            in the browser. There is no data source defined for this yet — it
            needs more analysis before it can be templatized.
          </span>
        </div>
      </div>
    </section>
  );
}

// ════════════ INVOICE DRAWER (read-only) ════════════
// Internal notes thread for an invoice (our side only, never pushed to QBO).
// Add notes and delete them (with confirmation). Inline-styled so it survives
// the `.arcc` reset.
const threadBtn = {
  padding: '6px 12px',
  borderRadius: 8,
  border: '1px solid #16463d',
  color: '#f6f6f6',
  font: 'inherit',
  fontSize: 12,
  fontWeight: 600,
} as const;

function CommentThread({
  comments,
  currentUserId,
  onAdd,
  onDelete,
}: {
  comments: ArInvoiceComment[];
  currentUserId: number | null;
  onAdd: (body: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}) {
  // Only the author of a note may delete it (also enforced on the backend).
  const canDelete = (c: ArInvoiceComment) =>
    c.authorUserId != null && c.authorUserId === currentUserId;
  const [text, setText] = useState('');
  const [adding, setAdding] = useState(false);
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const add = async () => {
    if (!text.trim()) return;
    setAdding(true);
    try {
      await onAdd(text.trim());
      setText('');
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id: number) => {
    setDeletingId(id);
    try {
      await onDelete(id);
      setConfirmingId(null);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ marginTop: 18 }}>
      <div
        style={{
          fontSize: 11,
          color: 'var(--txt-faint)',
          textTransform: 'uppercase',
          letterSpacing: '.07em',
          margin: '0 0 12px',
        }}
      >
        Comments
      </div>

      {comments.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--txt-faint)' }}>
          No notes yet.
        </div>
      ) : (
        comments.map((c) => (
          <div
            key={c.id}
            style={{
              background: 'var(--panel)',
              border: '1px solid var(--line)',
              borderRadius: 10,
              padding: 10,
              marginBottom: 8,
            }}
          >
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{c.body}</div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 8,
              }}
            >
              <span style={{ fontSize: 11, color: 'var(--txt-faint)' }}>
                {[
                  c.authorName,
                  c.createdAt ? formatDate(c.createdAt.slice(0, 10)) : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              {canDelete(c) &&
                (confirmingId === c.id ? (
                  <span style={{ display: 'inline-flex', gap: 8 }}>
                    <button
                      type='button'
                      disabled={deletingId === c.id}
                      // eslint-disable-next-line @typescript-eslint/no-misused-promises
                      onClick={() => remove(c.id)}
                      style={{ ...threadBtn, background: '#8a2e2e' }}
                    >
                      {deletingId === c.id ? 'Deleting…' : 'Delete'}
                    </button>
                    <button
                      type='button'
                      onClick={() => setConfirmingId(null)}
                      style={{ ...threadBtn, background: '#0a312b' }}
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    type='button'
                    onClick={() => setConfirmingId(c.id)}
                    style={{
                      ...threadBtn,
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--txt-faint)',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  >
                    Delete
                  </button>
                ))}
            </div>
          </div>
        ))
      )}

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder='Add a note…'
        rows={3}
        style={{
          width: '100%',
          marginTop: 8,
          background: 'var(--panel)',
          border: '1px solid var(--line)',
          borderRadius: 10,
          color: 'var(--txt)',
          font: 'inherit',
          fontSize: 13,
          padding: 10,
          resize: 'vertical',
        }}
      />
      <div
        style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}
      >
        <button
          type='button'
          disabled={adding || !text.trim()}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={add}
          style={{
            ...threadBtn,
            padding: '8px 16px',
            borderRadius: 10,
            fontSize: 13,
            background: '#0a6b5e',
            cursor: adding || !text.trim() ? 'default' : 'pointer',
            opacity: adding || !text.trim() ? 0.6 : 1,
          }}
        >
          {adding ? 'Adding…' : 'Add note'}
        </button>
      </div>
    </div>
  );
}

function InvoiceDrawer({
  inv,
  currentUserId,
  onAddComment,
  onDeleteComment,
  asOf,
  onClose,
}: {
  inv?: ArInvoice;
  currentUserId: number | null;
  onAddComment: (body: string) => Promise<void>;
  onDeleteComment: (id: number) => Promise<void>;
  asOf: string;
  onClose: () => void;
}) {
  if (!inv) return null;
  const od = daysOverdue(inv, asOf);
  const rows: [string, React.ReactNode][] = [
    ['Status', <StatusChip status={inv.status} key='s' />],
    [
      'Channel',
      inv.channel ? (
        <span className={`tag ${inv.channel}`}>{inv.channel}</span>
      ) : (
        '—'
      ),
    ],
    ['Partner', inv.partner && inv.partner !== 'N/A' ? inv.partner : 'Direct'],
    ['Invoice amount', formatUSD(inv.amount)],
    ['Amount paid', formatUSD(inv.amountPaid)],
    ['Outstanding', formatUSD(outstanding(inv))],
    ['Send date', formatDate(inv.sendDate)],
    ['Due date', formatDate(inv.dueDate)],
    ['Date paid', inv.datePaid ? formatDate(inv.datePaid) : '—'],
    [
      'Days overdue',
      od != null && od > 0 ? (
        <span style={{ color: 'var(--over)' }}>{od} days</span>
      ) : (
        '—'
      ),
    ],
    ['Contact (sent to)', inv.email ? inv.email : <NeedDef />],
    // Account owner / Net new-renewal / Contract term / Contract end / ARR —
    // source not defined yet (not sourced from QBO), so commented out for now.
    // ['Account owner', inv.accountOwner ? inv.accountOwner : <NeedDef />],
    // ['Contract term', inv.term ? inv.term : <NeedDef />],
    // ['Contract end', inv.contractEnd ? formatDate(inv.contractEnd) : <NeedDef />],
    // ['ARR', inv.arr != null ? formatUSD(inv.arr) : <NeedDef />],
    // ['Net new / renewal', inv.netNew ? inv.netNew : <NeedDef />],
    [
      'Invoice note',
      inv.invoiceStatus ? (
        <span style={{ whiteSpace: 'pre-line' }}>{inv.invoiceStatus}</span>
      ) : (
        '—'
      ),
    ],
    ['Customer memo', inv.customerMemo ? inv.customerMemo : '—'],
  ];
  const tl: [string, string][] = [];
  if (inv.sendDate) tl.push(['Invoice sent', formatDate(inv.sendDate)]);
  if (inv.dueDate) tl.push(['Payment due', formatDate(inv.dueDate)]);
  if (inv.datePaid) tl.push(['Paid', formatDate(inv.datePaid)]);

  return (
    <div className='arcc'>
      <div className='arcc-drawer-bg' onClick={onClose} />
      <aside className='arcc-drawer'>
        <div className='drawer-h'>
          <button type='button' className='x' onClick={onClose}>
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              style={{ width: 16, height: 16 }}
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
          <div className='dc'>{inv.customer}</div>
        </div>
        <div className='drawer-b'>
          {rows.map(([k, v]) => (
            <div className='drow' key={k}>
              <span className='dk'>{k}</span>
              <span className='dv'>{v}</span>
            </div>
          ))}
          {tl.length > 0 && (
            <div className='timeline'>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--txt-faint)',
                  textTransform: 'uppercase',
                  letterSpacing: '.07em',
                  margin: '18px 0 12px',
                }}
              >
                Timeline
              </div>
              {tl.map((t) => (
                <div className='tl-item' key={t[0]}>
                  <div className='tl-dot' />
                  <div className='tl-c'>
                    <div className='tt'>{t[0]}</div>
                    <div className='td'>{t[1]}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <CommentThread
            comments={inv.comments ?? []}
            currentUserId={currentUserId}
            onAdd={onAddComment}
            onDelete={onDeleteComment}
          />
        </div>
      </aside>
    </div>
  );
}

// ════════════ CUSTOMER 360 DRAWER ════════════
function CustomerDrawer({
  name,
  all,
  onInvoice,
  onClose,
}: {
  name: string;
  all: ArInvoice[];
  onInvoice: (id: string) => void;
  onClose: () => void;
}) {
  const invs = all
    .filter((r) => r.customer === name)
    .sort((a, b) => ((b.sendDate || '') < (a.sendDate || '') ? -1 : 1));
  const paid = invs
    .filter((r) => r.status === 'PAID')
    .reduce((a, b) => a + (b.amount ?? 0), 0);
  const open = invs
    .filter((r) => isOpen(r.status) && r.active)
    .reduce((a, b) => a + outstanding(b), 0);

  return (
    <div className='arcc'>
      <div className='arcc-drawer-bg' onClick={onClose} />
      <aside className='arcc-drawer'>
        <div className='drawer-h'>
          <button type='button' className='x' onClick={onClose}>
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              style={{ width: 16, height: 16 }}
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
          <div className='dc'>{name}</div>
        </div>
        <div className='drawer-b'>
          <div className='cstats' style={{ marginBottom: 20, gap: 22 }}>
            <div className='cst'>
              <div
                className='csv'
                style={{ fontSize: 20, fontFamily: 'var(--display)' }}
              >
                {formatUSD(paid)}
              </div>
              <div className='csl'>Lifetime paid</div>
            </div>
            <div className='cst'>
              <div
                className='csv'
                style={{
                  fontSize: 20,
                  fontFamily: 'var(--display)',
                  color: open > 0 ? 'var(--over)' : 'var(--txt)',
                }}
              >
                {formatUSD(open)}
              </div>
              <div className='csl'>Open balance</div>
            </div>
            <div className='cst'>
              <div
                className='csv'
                style={{ fontSize: 20, fontFamily: 'var(--display)' }}
              >
                {invs.length}
              </div>
              <div className='csl'>Invoices</div>
            </div>
          </div>
          <div className='drow'>
            <span className='dk'>Contact (sent to)</span>
            <span className='dv'>
              {invs[0]?.email ? invs[0].email : <NeedDef />}
            </span>
          </div>
          {/* ARR — not sourced from QBO for now.
          <div className='drow'>
            <span className='dk'>ARR</span>
            <span className='dv'>
              <NeedDef />
            </span>
          </div> */}
          <div
            style={{
              fontSize: 11,
              color: 'var(--txt-faint)',
              textTransform: 'uppercase',
              letterSpacing: '.07em',
              margin: '20px 0 10px',
            }}
          >
            Invoice history ({invs.length})
          </div>
          {invs.map((r) => (
            <div
              className='drow'
              style={{ cursor: 'pointer' }}
              key={r.invoiceId}
              onClick={() => onInvoice(r.invoiceId)}
            >
              <span className='dk'>
                {formatDateshort(r.sendDate)} · <StatusChip status={r.status} />
              </span>
              <span className='dv num'>{formatUSD(r.amount)}</span>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}

// ════════════ PRESENT MODE ════════════
function PresentMode({
  active,
  asOf,
  onClose,
}: {
  active: ArInvoice[];
  asOf: string;
  onClose: () => void;
}) {
  const list = useMemo(() => {
    const paid = active.filter(
      (r) =>
        r.status === 'PAID' &&
        r.datePaid &&
        (daysOverdue({ ...r, dueDate: r.datePaid }, asOf) ?? 99) <= 7
    );
    const overdue = active
      .filter((r) => r.status === 'Overdue')
      .sort((a, b) => ((a.sendDate || '') < (b.sendDate || '') ? -1 : 1));
    const out = active.filter(
      (r) => r.status === 'Outstanding' || r.status === 'Partial'
    );
    // Scheduled invoices — no source in QBO for now.
    // const sched = active.filter((r) => isSched(r));
    return [
      ...paid.map((r) => ({ r, stage: 'JUST PAID', color: 'var(--paid)' })),
      ...overdue.map((r) => ({ r, stage: 'OVERDUE', color: 'var(--over)' })),
      ...out.map((r) => ({ r, stage: 'OUTSTANDING', color: 'var(--out)' })),
      // ...sched.map((r) => ({ r, stage: 'SCHEDULED', color: 'var(--sched)' })),
    ];
  }, [active, asOf]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight')
        setIdx((i) => Math.min(i + 1, list.length - 1));
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(i - 1, 0));
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [list.length, onClose]);

  const item = list[idx];

  return (
    <div className='arcc'>
      <div className='arcc-present'>
        <div className='present-top'>
          <div
            className='present-brand'
            dangerouslySetInnerHTML={{ __html: OPSTART_MARK }}
          />
          <h2>Weekly review</h2>
          <div className='present-progress'>
            <span
              style={{
                width: list.length
                  ? `${((idx + 1) / list.length) * 100}%`
                  : '0%',
              }}
            />
          </div>
          <div className='present-count'>
            {list.length ? `${idx + 1} / ${list.length}` : '0 / 0'}
          </div>
          <button type='button' className='btn ghost' onClick={onClose}>
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              style={{ width: 14, height: 14 }}
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
            Exit
          </button>
        </div>
        <div className='present-stage'>
          <button
            type='button'
            className='present-nav prev'
            disabled={idx === 0}
            onClick={() => setIdx((i) => Math.max(i - 1, 0))}
          >
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              <polyline points='15 18 9 12 15 6' />
            </svg>
          </button>
          {item ? (
            <PresentCard item={item} asOf={asOf} />
          ) : (
            <div className='empty'>
              <div className='big'>Nothing active to review</div>
            </div>
          )}
          <button
            type='button'
            className='present-nav next'
            disabled={idx >= list.length - 1}
            onClick={() => setIdx((i) => Math.min(i + 1, list.length - 1))}
          >
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              <polyline points='9 18 15 12 9 6' />
            </svg>
          </button>
          <div className='kbd-hint'>
            <span>
              <kbd>←</kbd>
              <kbd>→</kbd> navigate
            </span>
            <span>
              <kbd>esc</kbd> exit
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function presentAsk(r: ArInvoice, stage: string) {
  if (stage === 'OVERDUE') {
    return (
      <>
        Overdue — <b>follow up</b> with {r.customer}.
      </>
    );
  }
  if (stage === 'JUST PAID') {
    return (
      <>
        ✓ Payment received — <b>{formatUSD(r.amount)}</b> on{' '}
        {formatDate(r.datePaid)}
      </>
    );
  }
  return (
    <>
      Sent, due {formatDate(r.dueDate)} — <b>on track</b>.
    </>
  );
}

function PresentCard({
  item,
  asOf,
}: {
  item: { r: ArInvoice; stage: string; color: string };
  asOf: string;
}) {
  const { r, stage, color } = item;
  const od = daysOverdue(r, asOf);
  return (
    <div className='icard'>
      <div
        className='ic-stage-tag'
        style={{ background: color, color: '#041014' }}
      >
        {stage}
      </div>
      <div className='ic-top'>
        <div>
          <div className='ic-cust'>{r.customer}</div>
          <div className='ic-sub'>
            <ChannelTag inv={r} />
            <StatusChip status={r.status} />
          </div>
        </div>
        <div className='ic-amt'>
          <div className='big' style={{ color }}>
            {formatUSD(r.amount)}
          </div>
          <div className='lbl'>
            {stage === 'JUST PAID' ? 'collected' : 'invoice amount'}
          </div>
        </div>
      </div>
      <div className='ic-grid'>
        <div className='ic-cell'>
          <div className='k'>Sent</div>
          <div className='v'>{formatDate(r.sendDate)}</div>
        </div>
        <div className='ic-cell'>
          <div className='k'>Due</div>
          <div
            className={`v ${stage === 'OVERDUE' && od && od > 0 ? 'over' : ''}`}
          >
            {formatDate(r.dueDate)}
            {stage === 'OVERDUE' && od && od > 0 ? ` · ${od}d overdue` : ''}
          </div>
        </div>
      </div>
      <div className='ic-ask'>
        <span className='q'>{presentAsk(r, stage)}</span>
      </div>
    </div>
  );
}

// ════════════ PAGE SHELL ════════════
// Minimal shape returned by transactions/getClientCompanies that the gate uses.
interface ClientCompany {
  id: number;
  name: string;
}

// Page header (icon + title) shown in the non-dashboard states, matching the
// other pages (Transactions, Files, Investor Reporting). The dashboard view has
// its own branding, so it doesn't use this.
function NoDashboardShell({ children }: { children: ReactNode }) {
  return (
    <div className='p-4'>
      <h1 className='text-lg font-bold uppercase mb-4'>
        <FontAwesomeIcon icon={faTable} size='lg' className='mr-4 text-white' />
        AR Dashboard
      </h1>
      {children}
    </div>
  );
}

function ArDashboardLoader({
  clientId,
  companyId,
  companyName,
  selector,
}: {
  clientId: number;
  companyId: number;
  companyName?: string;
  selector?: ReactNode;
}) {
  const {
    data: snapshot,
    error,
    isValidating,
  } = get<ArSnapshot>(
    `qbo/ar-snapshot?clientId=${clientId}&companyId=${companyId}`
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fire-and-forget: the endpoint answers 202 and rebuilds from QBO in the
  // background, so we just confirm and tell the user to check back — no polling.
  // They'll see the new data (and a fresher AS OF) on their next load.
  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Keep the spinner visible for a minimum time even though the POST returns
    // 202 almost immediately — makes it clear to the user something happened.
    const minSpin = delay(REFRESH_MIN_SPIN_MS);
    try {
      await Promise.all([
        post('qbo/ar-snapshot/refresh', { clientId, companyId }),
        minSpin,
      ]);
      alertMessageSuccess('Refreshing the data. Check back in a few minutes.');
    } catch {
      await minSpin;
      alertErrorMessage('Could not start the refresh. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!snapshot && isValidating) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spinner className='h-16' />
      </div>
    );
  }
  if (error)
    return (
      <ErrorAlert
        error={error as unknown}
        context='AR Command Center (template)'
      />
    );
  // Never generated yet: offer to build it (same background refresh).
  if (!snapshot)
    return (
      <NoDashboardShell>
        <div style={{ paddingTop: 40, paddingBottom: 30, textAlign: 'center' }}>
          <div style={{ marginBottom: 16, color: '#9db5ad' }}>
            No data yet for <b>{companyName ?? 'this company'}</b>.
          </div>
          <RefreshButton
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleRefresh}
            isRefreshing={isRefreshing}
            label='Generate now'
            busyLabel='Generating…'
            primary
          />
        </div>
        {selector ? (
          <div style={{ paddingBottom: 30, textAlign: 'center' }}>
            <div style={{ marginBottom: 16, color: '#9db5ad' }}>
              or select a new company
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {selector}
            </div>
          </div>
        ) : null}
      </NoDashboardShell>
    );
  return (
    <ArDashboardView
      snapshot={snapshot}
      selector={selector}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
    />
  );
}

// Resolves which company to load: fetches the client's companies, auto-loads
// when there's exactly one, and otherwise shows the company selector (centered
// until a company is chosen, then in the dashboard topbar) so the user can pick
// or switch between them.
function CompaniesGate({ clientId }: { clientId: number }) {
  const {
    data: companies,
    error,
    isValidating,
  } = get<ClientCompany[]>(`transactions/getClientCompanies/${clientId}`);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);

  if (!companies && isValidating) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spinner className='h-16' />
      </div>
    );
  }
  if (error)
    return <ErrorAlert error={error as unknown} context='AR Command Center' />;
  if (!companies || companies.length === 0)
    return (
      <NoDashboardShell>
        <div style={{ paddingTop: 40, textAlign: 'center', color: '#9DB5AD' }}>
          No companies found for this client.
        </div>
      </NoDashboardShell>
    );

  // Single company: nothing to choose, load it straight away.
  if (companies.length === 1)
    return (
      <ArDashboardLoader
        clientId={clientId}
        companyId={companies[0].id}
        companyName={companies[0].name}
      />
    );

  const selector = (
    <CompanySelectorDropdown
      companies={companies}
      selectedId={selectedId}
      onSelect={setSelectedId}
      placeholder='Select a company…'
    />
  );

  // Nothing chosen yet: show the selector, no dashboard below.
  if (!selectedId)
    return (
      <NoDashboardShell>
        <div
          style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}
        >
          {selector}
        </div>
      </NoDashboardShell>
    );

  return (
    <ArDashboardLoader
      clientId={clientId}
      companyId={selectedId}
      companyName={companies.find((c) => c.id === selectedId)?.name}
      selector={selector}
    />
  );
}

// The dashboard's display fonts (Inter + Fraunces), loaded on demand. Kept out
// of ArDashboard.css so the @import doesn't pull them for the whole SPA — they
// are only ever applied within `.arcc`.
/* eslint-disable max-len */
const ARCC_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&display=swap';
/* eslint-enable max-len */

export default function ArDashboard() {
  // Wrap in the app chrome (header + side menu) like the other pages. Note this
  // deliberately shows two side menus — the app's and the prototype's own rail.
  const { companyName, impersonatingAccountId, auth }: IUserContext =
    useUserContext();
  const navigate = useNavigate();

  if (auth?.isAdmin && !impersonatingAccountId) {
    navigate('/clients');
  }

  // Load the dashboard fonts only while this page is open, not app-wide.
  useEffect(() => {
    const id = 'arcc-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = ARCC_FONTS_HREF;
    document.head.appendChild(link);
  }, []);
  const clientId = impersonatingAccountId || auth?.clients?.[0]?.id;
  const { data: clients } = get<Client[]>('account/list');

  const content = clientId ? (
    <CompaniesGate clientId={clientId} />
  ) : (
    <NoDashboardShell>
      <div style={{ paddingTop: 40, textAlign: 'center', color: '#9DB5AD' }}>
        No client selected.
      </div>
    </NoDashboardShell>
  );

  return (
    <PageContainer name='ar-command-center-template'>
      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
      <DashboardWrapper
        companyName={companyName as string}
        companyData={clients}
      >
        {content}
      </DashboardWrapper>
    </PageContainer>
  );
}
